import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["modal", "title", "roleInput", "submitButton"]
  static values = { url: String }

  open(event) {
    event.preventDefault()

    const selectedIds = this.selectedIds()
    if (selectedIds.length === 0) return

    this.resetForm()
    this.updateTitle(selectedIds.length)
    Modal.getOrCreateInstance(this.modalTarget).show()
    this.validate()
  }

  validate() {
    if (!this.hasSubmitButtonTarget) return

    const roleSelected = this.roleInputTargets.some((input) => input.checked)
    this.submitButtonTarget.disabled = !roleSelected || this.submitButtonTarget.dataset.saving === "true"
  }

  async save(event) {
    event.preventDefault()

    const selectedIds = this.selectedIds()
    const role = this.selectedRole()
    if (selectedIds.length === 0 || !role || !this.hasUrlValue) return

    this.setSaving(true)

    const formData = new FormData()
    formData.append("_method", "PATCH")
    formData.append("role", role)
    selectedIds.forEach((id) => formData.append("company_user_ids[]", id))

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        data.updated.forEach((item) => this.updateRowRole(item.id, item.role, item.role_label))
        this.clearSelection()
        Modal.getOrCreateInstance(this.modalTarget).hide()
      } else {
        const message = data?.errors?.join(", ") || `HTTP ${response.status}`
        alert(`Failed to update roles: ${message}`)
      }
    } catch (error) {
      console.error("Error bulk updating roles:", error)
      alert("An error occurred. Please try again.")
    } finally {
      this.setSaving(false)
      this.validate()
    }
  }

  modalHidden() {
    this.resetForm()
    this.validate()
  }

  resetForm() {
    this.roleInputTargets.forEach((input) => {
      input.checked = false
    })
  }

  updateTitle(count) {
    if (!this.hasTitleTarget) return

    const noun = count === 1 ? "member" : "members"
    this.titleTarget.textContent = `Edit role for ${count} ${noun}`
  }

  selectedRole() {
    const selected = this.roleInputTargets.find((input) => input.checked)
    return selected?.value
  }

  selectedIds() {
    return [...this.element.querySelectorAll('[data-bulk-select-target="checkbox"]:checked')]
      .filter((checkbox) => !checkbox.closest("tr[hidden]"))
      .map((checkbox) => checkbox.value)
  }

  clearSelection() {
    const bulkSelectElement = this.element.querySelector('[data-controller~="bulk-select"]')
    if (!bulkSelectElement) return

    const controller = this.application.getControllerForElementAndIdentifier(bulkSelectElement, "bulk-select")
    controller?.clearSelection()
  }

  updateRowRole(companyUserId, role, roleLabel) {
    const checkbox = this.element.querySelector(
      `[data-bulk-select-target="checkbox"][value="${companyUserId}"]`
    )
    if (!checkbox) return

    const row = checkbox.closest("tr")
    const roleCell = row?.querySelector('[data-sort-key="role"]')
    const field = roleCell?.querySelector('[data-controller~="company-user-field"]')
    if (!roleCell || !field) return

    roleCell.dataset.sortValue = role
    roleCell.dataset.filterValue = role.toLowerCase()

    const hidden = field.querySelector(".select-hidden-input-value")
    const label = field.querySelector("[data-app-select-target='label']")
    const options = field.querySelectorAll("[data-app-select-target='option']")

    if (hidden) hidden.value = role
    if (label) label.textContent = roleLabel

    options.forEach((option) => {
      option.classList.toggle("app-select-option-selected", option.dataset.value === role)
    })

    const fieldController = this.application.getControllerForElementAndIdentifier(field, "company-user-field")
    if (fieldController) fieldController.previousValue = role
  }

  setSaving(saving) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.dataset.saving = saving ? "true" : "false"
    this.submitButtonTarget.textContent = saving ? "Saving..." : "Save Changes"
    this.submitButtonTarget.disabled = saving || !this.selectedRole()
  }
}
